import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { ArchetypeWithSubarchetypes } from "@liga/shared";
import {
  api,
  type MeleeAliasesResponse,
  type MeleeLoadResponse,
  type MeleePublishMatch,
  type MeleePublishRequest,
  type MeleePublishResponse,
  type MeleeReviewMatch,
  type MeleeReviewPayload,
  type MeleeReviewStanding,
} from "../api/client";
import { PlayerNameCombobox } from "./PlayerNameCombobox";
import { SubarchetypeSelect } from "./DecksAdminPanel";

interface FormState {
  meleeEventId: string;
  eventDate: string;
  eventName: string;
  leagueName: string;
  /**
   * Editorial "É um Open" checkbox — purely a local admin decision.
   * Melee doesn't return this, so we just forward the boolean to the
   * publish endpoint alongside the standings/matches payload.
   */
  isOpen: boolean;
}

interface StandingDraft {
  rank: number;
  meleeName: string;
  playerName: string; // resolved (editable)
  points: number;
  deckName: string | null;
  /**
   * Subarchetype id selected by the admin for this row's deck.
   * Initialised from the API's auto-suggestion (`suggestedSubarchetypeId`).
   * `null` means "no subarchetype yet" — at publish time the resulting
   * `event_result` is left unclassified ("Sem classificação").
   */
  subarchetypeId: number | null;
  /**
   * melee.gg decklist id; forwarded as-is to publish so the back-end can
   * snapshot the cards. `null` when the standing has no decklist attached.
   */
  meleeDecklistId: string | null;
}

interface MatchDraft {
  round: number;
  meleeResultString: string;
  player1MeleeName: string;
  player2MeleeName: string | null;
  player1Name: string;
  player2Name: string; // empty string when bye
  player1Wins: number;
  player2Wins: number;
  draws: number;
  isIntentionalDraw: boolean;
  isBye: boolean;
  winnerSelection: "p1" | "p2" | "draw";
  needsReview: boolean;
  reviewReason?: string;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * Top-level container for the Melee import flow:
 *   1. Form to enter event metadata + Carregar do Melee
 *   2. Standings review table with editable player names (free-text combobox)
 *   3. Matches review (grouped by round), needs-review rows highlighted
 *   4. Publicar button -> POST /publish, navigate to /events/<id> on success
 */
export function MeleeImportPanel() {
  const [form, setForm] = useState<FormState>({
    meleeEventId: "",
    eventDate: todayIso(),
    eventName: "",
    leagueName: "",
    isOpen: false,
  });
  const [loaded, setLoaded] = useState<MeleeLoadResponse | null>(null);
  const [standings, setStandings] = useState<StandingDraft[]>([]);
  const [matches, setMatches] = useState<MatchDraft[]>([]);
  const [publishError, setPublishError] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const aliasesQuery = useQuery<MeleeAliasesResponse>({
    queryKey: ["admin", "melee", "aliases"],
    queryFn: api.meleeAliases,
  });

  const archetypesQuery = useQuery<ArchetypeWithSubarchetypes[]>({
    queryKey: ["admin", "archetypes"],
    queryFn: api.adminArchetypes,
  });

  const suggestionPool = useMemo(() => {
    const set = new Set<string>();
    for (const p of aliasesQuery.data?.players ?? []) set.add(p);
    for (const a of aliasesQuery.data?.aliases ?? []) set.add(a.playerName);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [aliasesQuery.data]);

  const loadMutation = useMutation({
    mutationFn: api.meleeLoad,
    onSuccess: (data) => {
      setLoaded(data);
      setStandings(initStandings(data.payload));
      setMatches(initMatches(data.payload));
      setPublishError(null);
    },
  });

  const upsertAliasMutation = useMutation({
    mutationFn: api.meleeUpsertAlias,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "melee", "aliases"] });
    },
  });

  const publishMutation = useMutation<
    MeleePublishResponse,
    Error,
    MeleePublishRequest
  >({
    mutationFn: api.meleePublish,
    onSuccess: ({ eventId }) => {
      navigate(`/events/${eventId}`);
    },
    onError: (err) => {
      setPublishError(err.message);
    },
  });

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-line bg-white p-6">
        <div className="text-xs uppercase tracking-[0.18em] text-[#8a8170] font-bold mb-1.5">
          Importação
        </div>
        <h2 className="font-display text-xl font-bold text-ink mb-1">
          Carregar evento do Melee
        </h2>
        <p className="text-[14px] leading-relaxed text-[#6b6356] mb-4">
          Informe o ID do evento no Melee e os detalhes do evento. Em seguida,
          revise os jogadores e partidas antes de publicar.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            loadMutation.mutate({
              meleeEventId: form.meleeEventId.trim(),
              eventDate: form.eventDate,
              eventName: form.eventName.trim(),
              leagueName: form.leagueName.trim() || null,
            });
          }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <Field
            label="Melee.gg Event Id"
            value={form.meleeEventId}
            onChange={(v) => setForm({ ...form, meleeEventId: v })}
            placeholder="427830"
            required
          />
          <Field
            label="Data do evento"
            type="date"
            value={form.eventDate}
            onChange={(v) => setForm({ ...form, eventDate: v })}
            required
          />
          <Field
            label="Nome do evento"
            value={form.eventName}
            onChange={(v) => setForm({ ...form, eventName: v })}
            placeholder="9a Etapa Liga Abr/Mai 2026"
            required
          />
          <Field
            label="Liga (opcional)"
            value={form.leagueName}
            onChange={(v) => setForm({ ...form, leagueName: v })}
            placeholder="Liga Abr/Mai 2026"
          />
          <div className="md:col-span-2">
            <label className="flex items-start gap-2.5 cursor-pointer select-none rounded-lg border border-line bg-[#faf7ee] px-3 py-2.5">
              <input
                type="checkbox"
                checked={form.isOpen}
                onChange={(e) =>
                  setForm({ ...form, isOpen: e.target.checked })
                }
                className="mt-0.5 h-4 w-4 accent-brand-700"
              />
              <span>
                <span className="block text-sm font-semibold text-ink">
                  Marcar como Open
                </span>
                <span className="block text-xs text-[#6b6356] mt-0.5">
                  Este evento aparecerá na aba pública <strong>Opens</strong>,
                  agrupado por ano. Não vem do Melee — é apenas um marcador
                  editorial.
                </span>
              </span>
            </label>
          </div>
          <div className="md:col-span-2 flex items-center gap-3">
            <button
              type="submit"
              disabled={loadMutation.isPending || !form.meleeEventId.trim()}
              className="rounded-lg bg-brand-700 hover:bg-brand-800 disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2 text-sm font-semibold transition-colors"
            >
              {loadMutation.isPending ? "Carregando..." : "Carregar do Melee"}
            </button>
            {loadMutation.error && (
              <span className="text-sm text-red-700">
                {loadMutation.error.message}
              </span>
            )}
          </div>
        </form>
      </section>

      {loaded && (
        <ReviewSection
          loaded={loaded}
          standings={standings}
          setStandings={setStandings}
          matches={matches}
          setMatches={setMatches}
          suggestionPool={suggestionPool}
          archetypes={archetypesQuery.data ?? []}
          onAliasCommit={(meleeName, playerName) => {
            if (!meleeName || !playerName) return;
            if (meleeName.trim() === playerName.trim()) return;
            upsertAliasMutation.mutate({ meleeName, playerName });
          }}
          publishError={publishError}
          isPublishing={publishMutation.isPending}
          onPublish={() => {
            setPublishError(null);
            publishMutation.mutate(
              buildPublishBody(loaded, standings, matches, form.isOpen),
            );
          }}
        />
      )}
    </div>
  );
}

// ---------- review section ---------------------------------------------------

interface ReviewSectionProps {
  loaded: MeleeLoadResponse;
  standings: StandingDraft[];
  setStandings: (next: StandingDraft[]) => void;
  matches: MatchDraft[];
  setMatches: (next: MatchDraft[]) => void;
  suggestionPool: string[];
  archetypes: ArchetypeWithSubarchetypes[];
  onAliasCommit: (meleeName: string, playerName: string) => void;
  publishError: string | null;
  isPublishing: boolean;
  onPublish: () => void;
}

function ReviewSection({
  loaded,
  standings,
  setStandings,
  matches,
  setMatches,
  suggestionPool,
  archetypes,
  onAliasCommit,
  publishError,
  isPublishing,
  onPublish,
}: ReviewSectionProps) {
  const unresolvedCount = standings.filter((s) => !s.playerName.trim()).length;
  const reviewCount = matches.filter((m) => m.needsReview).length;
  const matchesUnresolved = matches.filter(
    (m) => !m.player1Name.trim() || (m.player2MeleeName && !m.player2Name.trim()),
  ).length;

  const matchesByRound = useMemo(() => {
    const by: Record<number, MatchDraft[]> = {};
    matches.forEach((m, idx) => {
      const bucket = by[m.round] ?? (by[m.round] = []);
      // Stamp the array index so updates know which row to mutate.
      bucket.push({ ...m, __idx: idx } as unknown as MatchDraft);
    });
    return by;
  }, [matches]);

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-line bg-white p-6 space-y-4">
        <header className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#8a8170] font-bold mb-1.5">
              Standings — {loaded.payload.numberOfPlayers} jogadores
            </div>
            <h3 className="font-display text-lg font-bold text-ink">
              {loaded.eventName}{" "}
              <span className="text-[#8a8170] font-normal">· {loaded.eventDate}</span>
            </h3>
            {loaded.leagueName ? (
              <p className="text-sm text-[#6b6356]">
                Liga: <strong>{loaded.leagueName}</strong>
              </p>
            ) : (
              <p className="text-sm text-amber-700">
                Sem liga — este evento será salvo como avulso.
              </p>
            )}
          </div>
          {unresolvedCount > 0 && (
            <div className="text-sm bg-amber-50 border border-amber-300 text-amber-800 rounded px-3 py-1.5">
              {unresolvedCount} jogador(es) sem nome resolvido
            </div>
          )}
        </header>

        {archetypes.length === 0 && (
          <p className="text-xs bg-amber-50 border border-amber-300 text-amber-800 rounded px-3 py-1.5">
            Nenhum subarquétipo cadastrado ainda. Cadastre em{" "}
            <Link to="/theadmin/archetypes" className="font-semibold underline">
              Arquétipos
            </Link>{" "}
            para poder classificar os decks deste evento.
          </p>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-50 text-brand-900">
              <tr className="text-[11px] uppercase tracking-wide">
                <th className="text-left px-3 py-2 w-12 font-bold">#</th>
                <th className="text-left px-3 py-2 font-bold">Nome no Melee</th>
                <th className="text-left px-3 py-2 font-bold">Jogador (resolvido)</th>
                <th className="text-left px-3 py-2 font-bold">Deck</th>
                <th className="text-left px-3 py-2 w-64 font-bold">
                  Subarquétipo
                </th>
                <th className="text-right px-3 py-2 w-20 font-bold">Pontos</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((row, idx) => {
                const invalid = !row.playerName.trim();
                return (
                  <tr key={idx} className="border-t border-[#f3efe6] align-top">
                    <td className="px-3 py-2 tabular-nums text-[#9a9182]">{row.rank}</td>
                    <td className="px-3 py-2 font-medium text-ink whitespace-nowrap">
                      {row.meleeName}
                    </td>
                    <td className="px-3 py-2 min-w-[18rem]">
                      <PlayerNameCombobox
                        value={row.playerName}
                        onChange={(v) => {
                          const next = standings.slice();
                          next[idx] = { ...row, playerName: v };
                          setStandings(next);
                          // Keep Partidas in sync: any match row that references
                          // this melee name gets the freshly chosen resolved
                          // name, so the admin doesn't have to retype it.
                          setMatches(
                            propagatePlayerName(matches, row.meleeName, v),
                          );
                        }}
                        options={suggestionPool}
                        invalid={invalid}
                        ariaLabel={`Jogador resolvido para ${row.meleeName}`}
                        placeholder="Selecione ou digite um nome"
                      />
                      {invalid && (
                        <span className="text-xs text-amber-700 mt-1 block">
                          Informe o nome correspondente no banco.
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[#6b6356]">{row.deckName ?? "—"}</td>
                    <td className="px-3 py-2">
                      {row.deckName ? (
                        <SubarchetypeSelect
                          archetypes={archetypes}
                          value={row.subarchetypeId}
                          onChange={(next) => {
                            const arr = standings.slice();
                            arr[idx] = { ...row, subarchetypeId: next };
                            setStandings(arr);
                          }}
                          ariaLabel={`Subarquétipo para ${row.deckName}`}
                        />
                      ) : (
                        <span className="text-xs text-[#9a9182] italic">
                          sem deck
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-display font-bold text-ink">
                      {row.points}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-2xl border border-line bg-white p-6 space-y-4">
        <header className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#8a8170] font-bold mb-1.5">
              Partidas — {matches.length} no total
            </div>
            <h3 className="font-display text-lg font-bold text-ink">
              Resultados por rodada
            </h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {reviewCount > 0 && (
              <span className="text-sm bg-amber-50 border border-amber-300 text-amber-800 rounded px-3 py-1.5">
                {reviewCount} resultado(s) precisam de revisão
              </span>
            )}
            {matchesUnresolved > 0 && (
              <span className="text-sm bg-red-50 border border-red-300 text-red-800 rounded px-3 py-1.5">
                {matchesUnresolved} jogador(es) sem nome
              </span>
            )}
          </div>
        </header>

        {Object.entries(matchesByRound)
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([roundKey, rows]) => (
            <div key={roundKey} className="space-y-2">
              <h4 className="font-display font-bold text-ink">Rodada {roundKey}</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-brand-50 text-brand-900">
                    <tr className="text-[11px] uppercase tracking-wide">
                      <th className="text-left px-3 py-2 font-bold w-1/3">Jogador 1</th>
                      <th className="text-center px-3 py-2 font-bold w-32">Placar</th>
                      <th className="text-left px-3 py-2 font-bold w-1/3">Jogador 2</th>
                      <th className="text-left px-3 py-2 font-bold w-36">Vencedor</th>
                      <th className="text-left px-3 py-2 font-bold">Melee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const idx = (row as unknown as { __idx: number }).__idx;
                      const onPatch = (patch: Partial<MatchDraft>) => {
                        const next = matches.slice();
                        next[idx] = { ...row, ...patch } as MatchDraft;
                        setMatches(next);
                      };
                      return (
                        <MatchRow
                          key={idx}
                          row={row}
                          suggestionPool={suggestionPool}
                          onPatch={onPatch}
                          onAliasCommit={onAliasCommit}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
      </section>

      <section className="rounded-2xl border border-line bg-white p-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-bold text-ink">Pronto para publicar?</h3>
          <p className="text-sm text-[#6b6356]">
            Salvar este evento e suas partidas no banco de dados. Você pode
            re-publicar mais tarde se precisar corrigir algo (operação
            idempotente pelo ID do Melee).
          </p>
          {publishError && (
            <p className="text-sm text-red-700 mt-2">{publishError}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onPublish}
          disabled={
            isPublishing ||
            unresolvedCount > 0 ||
            matchesUnresolved > 0
          }
          className="rounded-lg bg-brand-700 hover:bg-brand-800 disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2 text-sm font-semibold transition-colors"
        >
          {isPublishing ? "Publicando..." : "Publicar evento"}
        </button>
      </section>
    </div>
  );
}

interface MatchRowProps {
  row: MatchDraft;
  suggestionPool: string[];
  onPatch: (patch: Partial<MatchDraft>) => void;
  onAliasCommit: (meleeName: string, playerName: string) => void;
}

function MatchRow({ row, suggestionPool, onPatch, onAliasCommit }: MatchRowProps) {
  return (
    <tr
      className={[
        "border-t border-[#f3efe6] align-top",
        row.needsReview ? "bg-amber-50/60" : "",
      ].join(" ")}
    >
      <td className="px-3 py-2 space-y-1">
        <div className="text-[12px] uppercase tracking-wide text-[#8a8170]">
          {row.player1MeleeName}
        </div>
        <PlayerNameCombobox
          value={row.player1Name}
          onChange={(v) => onPatch({ player1Name: v })}
          options={suggestionPool}
          invalid={!row.player1Name.trim()}
          ariaLabel={`Jogador 1 (${row.player1MeleeName})`}
          placeholder="Jogador 1"
        />
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center justify-center gap-1 tabular-nums font-display">
          <ScoreInput
            value={row.player1Wins}
            onChange={(v) => onPatch({ player1Wins: v })}
          />
          <span className="text-[#9a9182]">–</span>
          <ScoreInput
            value={row.player2Wins}
            onChange={(v) => onPatch({ player2Wins: v })}
          />
          <span className="text-[#9a9182]">–</span>
          <ScoreInput
            value={row.draws}
            onChange={(v) => onPatch({ draws: v })}
          />
        </div>
        <label className="mt-1 flex items-center gap-1.5 text-xs text-[#6b6356] cursor-pointer">
          <input
            type="checkbox"
            checked={row.isIntentionalDraw}
            onChange={(e) => onPatch({ isIntentionalDraw: e.target.checked })}
          />
          Empate intencional
        </label>
      </td>
      <td className="px-3 py-2 space-y-1">
        {row.isBye ? (
          <span className="italic text-slate-400 block px-2 py-1">bye</span>
        ) : (
          <>
            <div className="text-[12px] uppercase tracking-wide text-[#8a8170]">
              {row.player2MeleeName ?? "—"}
            </div>
            <PlayerNameCombobox
              value={row.player2Name}
              onChange={(v) => onPatch({ player2Name: v })}
              options={suggestionPool}
              invalid={!!row.player2MeleeName && !row.player2Name.trim()}
              ariaLabel={`Jogador 2 (${row.player2MeleeName ?? "bye"})`}
              placeholder="Jogador 2"
            />
          </>
        )}
      </td>
      <td className="px-3 py-2">
        <select
          aria-label="Vencedor"
          value={row.winnerSelection}
          onChange={(e) =>
            onPatch({
              winnerSelection: e.target.value as MatchDraft["winnerSelection"],
            })
          }
          className="w-full rounded border border-slate-300 px-2 py-1 text-sm bg-white"
        >
          <option value="p1">Jogador 1</option>
          {!row.isBye && <option value="p2">Jogador 2</option>}
          <option value="draw">Empate</option>
        </select>
        <button
          type="button"
          onClick={() => {
            if (row.player1MeleeName && row.player1Name.trim()) {
              onAliasCommit(row.player1MeleeName, row.player1Name.trim());
            }
            if (row.player2MeleeName && row.player2Name.trim()) {
              onAliasCommit(row.player2MeleeName, row.player2Name.trim());
            }
          }}
          className="mt-1 text-xs text-brand-700 hover:underline"
        >
          Salvar mapeamentos
        </button>
      </td>
      <td className="px-3 py-2 text-[12px] text-[#6b6356]">
        <span className="font-mono break-words">{row.meleeResultString}</span>
        {row.reviewReason && (
          <p className="text-amber-800 mt-1">{row.reviewReason}</p>
        )}
      </td>
    </tr>
  );
}

function ScoreInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <input
      type="number"
      min={0}
      value={Number.isFinite(value) ? value : 0}
      onChange={(e) => {
        const n = Number(e.target.value);
        onChange(Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0);
      }}
      className="w-12 rounded border border-slate-300 px-1.5 py-1 text-center font-display tabular-nums"
    />
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  required = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
}) {
  return (
    <label className="block text-sm">
      <span className="block text-[#6b6356] mb-1 font-medium">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full rounded border border-slate-300 bg-white px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500"
      />
    </label>
  );
}

// ---------- helpers ----------------------------------------------------------

function initStandings(payload: MeleeReviewPayload): StandingDraft[] {
  return payload.standings.map(
    (s: MeleeReviewStanding): StandingDraft => ({
      rank: s.rank,
      meleeName: s.meleeName,
      playerName: s.resolvedPlayerName ?? "",
      points: s.points,
      deckName: s.deckName,
      subarchetypeId: s.suggestedSubarchetypeId,
      meleeDecklistId: s.meleeDecklistId,
    }),
  );
}

function initMatches(payload: MeleeReviewPayload): MatchDraft[] {
  return payload.matches.map((m: MeleeReviewMatch): MatchDraft => {
    let winnerSelection: MatchDraft["winnerSelection"];
    if (m.isBye) {
      winnerSelection = "p1";
    } else if (m.winnerMeleeName === null) {
      winnerSelection = "draw";
    } else if (m.winnerMeleeName === m.player1MeleeName) {
      winnerSelection = "p1";
    } else {
      winnerSelection = "p2";
    }
    return {
      round: m.round,
      meleeResultString: m.meleeResultString,
      player1MeleeName: m.player1MeleeName,
      player2MeleeName: m.player2MeleeName,
      player1Name: m.player1ResolvedName ?? "",
      player2Name: m.player2ResolvedName ?? "",
      player1Wins: m.player1Wins,
      player2Wins: m.player2Wins,
      draws: m.draws,
      isIntentionalDraw: m.isIntentionalDraw,
      isBye: m.isBye,
      winnerSelection,
      needsReview: m.needsReview,
      reviewReason: m.reviewReason,
    };
  });
}

function buildPublishBody(
  loaded: MeleeLoadResponse,
  standings: StandingDraft[],
  matches: MatchDraft[],
  isOpen: boolean,
): MeleePublishRequest {
  return {
    meleeEventId: loaded.meleeEventId,
    eventDate: loaded.eventDate,
    eventName: loaded.eventName,
    leagueName: loaded.leagueName,
    // Only forward when the admin ticked it. Sending `false` on re-publish
    // would still preserve the flag (see the back-end note), but explicitly
    // omitting the field keeps the intent clean.
    isOpen: isOpen || undefined,
    standings: standings.map((s) => ({
      rank: s.rank,
      meleeName: s.meleeName,
      playerName: s.playerName.trim(),
      points: s.points,
      deckName: s.deckName,
      // Only forward `subarchetypeId` when there's actually a deck to
      // classify; otherwise the back-end has nothing to set it on.
      subarchetypeId: s.deckName ? s.subarchetypeId : undefined,
      meleeDecklistId: s.meleeDecklistId,
    })),
    matches: matches.map((m): MeleePublishMatch => {
      const player1Name = m.player1Name.trim();
      const player2Name = m.isBye ? null : m.player2Name.trim() || null;
      let winnerName: string | null;
      if (m.winnerSelection === "draw") winnerName = null;
      else if (m.winnerSelection === "p1") winnerName = player1Name;
      else winnerName = player2Name;
      return {
        round: m.round,
        player1MeleeName: m.player1MeleeName,
        player2MeleeName: m.player2MeleeName,
        player1Name,
        player2Name,
        player1Wins: m.player1Wins,
        player2Wins: m.player2Wins,
        draws: m.draws,
        isIntentionalDraw: m.isIntentionalDraw,
        winnerName,
      };
    }),
    aliases: collectAliases(standings, matches),
  };
}

/**
 * Returns a new matches array where every row whose `player1MeleeName` (or
 * `player2MeleeName`, unless it's a bye) matches `meleeName` has its resolved
 * player name set to `resolvedName`. The melee names from the API are already
 * canonical, but we compare case-insensitively as a safety net.
 */
function propagatePlayerName(
  matches: MatchDraft[],
  meleeName: string,
  resolvedName: string,
): MatchDraft[] {
  const needle = meleeName.trim().toLowerCase();
  if (!needle) return matches;
  let changed = false;
  const next = matches.map((m) => {
    const patch: Partial<MatchDraft> = {};
    if (m.player1MeleeName.trim().toLowerCase() === needle) {
      patch.player1Name = resolvedName;
    }
    if (
      !m.isBye &&
      m.player2MeleeName &&
      m.player2MeleeName.trim().toLowerCase() === needle
    ) {
      patch.player2Name = resolvedName;
    }
    if (Object.keys(patch).length === 0) return m;
    changed = true;
    return { ...m, ...patch };
  });
  return changed ? next : matches;
}

function collectAliases(
  standings: StandingDraft[],
  matches: MatchDraft[],
): Array<{ meleeName: string; playerName: string }> {
  const seen = new Map<string, string>();
  const add = (mele: string | null | undefined, player: string) => {
    if (!mele) return;
    const m = mele.trim();
    const p = player.trim();
    if (!m || !p) return;
    seen.set(m.toLowerCase(), p);
  };
  for (const s of standings) add(s.meleeName, s.playerName);
  for (const m of matches) {
    add(m.player1MeleeName, m.player1Name);
    if (!m.isBye) add(m.player2MeleeName, m.player2Name);
  }
  // Use the canonical melee name (original casing) from the first occurrence.
  const canonByLower = new Map<string, string>();
  const collectMelee = (mele: string | null | undefined) => {
    if (!mele) return;
    const m = mele.trim();
    if (!m) return;
    if (!canonByLower.has(m.toLowerCase())) canonByLower.set(m.toLowerCase(), m);
  };
  for (const s of standings) collectMelee(s.meleeName);
  for (const m of matches) {
    collectMelee(m.player1MeleeName);
    if (!m.isBye) collectMelee(m.player2MeleeName);
  }
  return [...seen.entries()].map(([lower, player]) => ({
    meleeName: canonByLower.get(lower) ?? lower,
    playerName: player,
  }));
}
